import React from "react";
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment-timezone";
import { connect } from "react-redux";
import { debounce } from "lodash";
import uniq from "lodash/uniq";
import {
  Alert,
  Icon,
  Layout,
  Input,
  Button,
  Table,
  Switch,
  Modal,
  Badge,
  message,
  Tooltip,
  Select,
  Checkbox, DatePicker,
} from "antd";

import classes from "./AuditLogs.css";

import isAuthenticated from "../../utils/isAuthenticated";
import {
  getAuditLogsRequest,
  deleteAuditLogsRequest,
  activateAuditLogsRequest,
  unreviewedAuditLogsRequest,
  reviewedAuditLogsRequest,
  resendAuditLogsRequest,
  getSitesRequest,
  reviewedAuditLogsBulkRequest,
  unreviewedAuditLogsBulkRequest,
  getAuditLogsActiveTypes,
} from "../../api/requests";
import { logout } from "../../reducers/user/actions";
import {
  getAuditLogDeletePermission,
  getAuditLogUpdatePermission,
  getAuditLogReviewPermission,
  getAuditLogReviewBulkPermission,
  getUserTimezone,
  getUserShortTimezone,
} from "../../reducers/user/selectors";
import AuditForm from "../../components/AuditForm/AuditForm";
import { documentTypes } from "../../components/DocumentForm/DocumentForm";
import actionTypes from "./actionTypes";
import { withNamespaces } from "react-i18next";

const { Content } = Layout;
const { Option } = Select;

const errorLevelColors = {
  none: "#52c41a",
  low: "#c4c400",
  medium: "#c49904",
  high: "#ff2702",
  critical: "#c40200",
};

const logResultTypes = {
  success: "#52c41a",
  error: "#f00",
  info: "#04c49e",
  debug: "#fdf39a",
};

class AuditLogs extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      audits: [],
      nameSearch: null,
      errorMessage: null,
      responseMessage: null,
      isLoading: false,
      sites: [],
      site: null,
      filter: null,
      order: "desc",
      currentPage: 1,
      total: 1,
      pageSize: 25,
      eventVisibleDropdown: false,
      sortedInfo: {},
      auditEdit: null,
      filterBy: [],
      filterValue: [],
      filtersBy: ["log_level"], // be default hide all data read logs like "sites viewed"
      filtersValue: ["actions"], // or permissions viewed, these are moved to log level info
      approvedSelected: null,
      filterDateFrom: null,
      filterDateTo: null,
      includeListEvent: true,
      shiftDown: false,
      checkedItems: [],
      auditDetails: null,
      activeTypes: [],
    };
  }

  handleKeyDown = (event) => {
    if (event.keyCode === 16) {
      this.setState({
        shiftDown: true,
      });
    }
  };

  handleKeyUp = (event) => {
    if (event.keyCode === 16) {
      this.setState({
        shiftDown: false,
      });
    }
  };

  async componentDidMount() {
    this.onNameSearch();
    this.getAuditLogsActiveTypes();

    try {
      const { data } = await getSitesRequest();
      this.setState({ sites: data });
    } catch (e) {
      if (e.response.status === 401) {
        this.props.logout();
        return;
      }
      console.log(e);
    }

    document.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("keyup", this.handleKeyUp);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeyDown);
    document.removeEventListener("keyup", this.handleKeyUp);
  }

  getAuditLogsActiveTypes = async () => {
    try {
      const { data: { data } } = await getAuditLogsActiveTypes();
      this.setState({
        activeTypes: data,
      });
    } catch (e) {
      console.log(e);
    }
  };

  onNameSearch = async (value) => {
    this.setState(
      { nameSearch: value, filter: null, site: null, currentPage: 1 },
      this.getAudits
    );
  };

  onNameChange = (e) => {
    this.setState({ nameSearch: e.target.value });
  };

  getAudits = debounce(async function (
    page = this.state.currentPage,
    per_page = this.state.pageSize
  ) {
    this.setState({
      errorMessage: null,
      isLoading: true,
      responseMessage: null,
    });

    try {
      const { data } = await getAuditLogsRequest({
        nameSearch: this.state.nameSearch,
        order_direction: this.state.order,
        page,
        per_page,
        order_column: get(this.state, "sortedInfo.columnKey"),
        filter_by: this.state.filtersBy,
        filter_value: this.state.filtersValue,
        filter_approve: this.state.approvedSelected,
        hide_audit_log_type: this.state.includeListEvent,
        filter_date_from: this.state.filterDateFrom,
        filter_date_to: this.state.filterDateTo,
      });

      if (Array.isArray(data.data)) {
        this.setState({
          checkedItems: [],
        });

        this.setState({
          audits: data.data,
          total: data.total_pages * per_page,
          currentPage: page,
        });
      } else {
        this.setState({
          audits: [data],
          total: 1 * per_page,
          currentPage: page,
        });
      }
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      this.setState({
        audits: [],
        errorMessage: get(e, "response.data.error", "Error"),
      });
    }

    this.setState({ isLoading: false });
  },
  500);

  clearFilters = () => {
    this.setState(
      {
        nameSearch: null,
        site: null,
        filter: null,
        filterValue: null,
        filterBy: null,
        sortedInfo: null,
        approvedSelected: null,
        filterDateFrom: null,
        filterDateTo: null,
        currentPage: 1,
      },
      this.getAudits
    );
  };

  handleFilterChange = (index) => (value) => {
    this.setState((state) => {
      const filtersBy = cloneDeep(state.filtersBy);
      const filtersValue = cloneDeep(state.filtersValue);
      filtersBy[index] = value;
      delete filtersValue[index];
      return { filtersBy, filtersValue };
    });
  };

  handleFilterValueChange = (index) =>
    debounce((value) => {
      this.setState((state) => {
        const filtersValue = cloneDeep(state.filtersValue);
        filtersValue[index] = value;
        return { filtersValue, currentPage: 1 };
      }, this.getAudits);
    }, 500);

  clearFilter = (index) => () => {
    this.setState((state) => {
      const filtersBy = cloneDeep(state.filtersBy);
      const filtersValue = cloneDeep(state.filtersValue);

      filtersBy.splice(index, 1);
      filtersValue.splice(index, 1);

      return { filtersBy, filtersValue, currentPage: 1,
        filterDateFrom: null,
        filterDateTo: null};
    }, this.getAudits);
  };

  onSelectApprovedStatus = (approvedStatusType) => () => {
    let status;
    const { approvedSelected } = this.state;

    if (approvedSelected === null && approvedStatusType === "approved") {
      status = false;
    }
    if (approvedSelected === true && approvedStatusType === "approved") {
      status = false;
    }
    if (approvedSelected === false && approvedStatusType === "approved") {
      status = null;
    }

    if (approvedSelected === null && approvedStatusType === "unapproved") {
      status = true;
    }
    if (approvedSelected === false && approvedStatusType === "unapproved") {
      status = true;
    }
    if (approvedSelected === true && approvedStatusType === "unapproved") {
      status = null;
    }

    this.setState(
      (_) => ({ approvedSelected: status, currentPage: 1 }),
      this.getAudits
    );
  };

  handleTableChange = (pagination, filters, sorter) => {
    this.setState(
      {
        isLoading: true,
        pageSize: pagination.pageSize,
        sortedInfo: sorter,
        order:
          get(sorter, "order") === "ascend"
            ? "asc"
            : get(sorter, "order") === "descend"
            ? "desc"
            : this.state.order,
      },
      () => this.getAudits(pagination.current, pagination.pageSize)
    );
  };

  onAuditEdit = (audit) => () => {
    this.setState({ auditEdit: audit });
  };

  onAuditEditHide = () => {
    this.setState({ auditEdit: null });
  };

  onAuditEditSubmit = () => {
    this.setState({ auditEdit: null }, this.getAudits);
  };

  onViewAuditDetails = (audit) => () => {
    this.setState({ auditDetails: audit });
  };

  onCloseAuditDetails = () => {
    this.setState({ auditDetails: null });
  };

  onSwitchAuditActivation = (id, state) => async () => {
    try {
      if (state === "active") {
        await deleteAuditLogsRequest(id);
      } else {
        await activateAuditLogsRequest(id);
      }

      message.success(this.props.i18n.t("Saved"));

      this.getAudits();
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      message.error("Error");
      console.log(e);
    }
  };

  onResendClick = (id) => async () => {
    try {
      const result = await resendAuditLogsRequest(id);
      const msg = get(
        result,
        "data.message",
        this.props.i18n.t("NamespaceAuditLogs:Email resent.")
      );

      message.success(msg);

      this.getAudits();
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      message.error(get(e, "response.data.error", "Error!"));
      console.log(e);
    }
  };

  onSwitchAuditReview = (id, reviewed) => async () => {
    try {
      if (reviewed) {
        await unreviewedAuditLogsRequest(id);
      } else {
        await reviewedAuditLogsRequest(id);
      }

      message.success(this.props.i18n.t("Saved"));

      this.getAudits();
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      message.error("Error");
      console.log(e);
    }
  };

  renderFilterItem = (index = this.state.filtersBy.length) => {
    const { filtersBy, filtersValue, sites } = this.state;

    return (
      <div className={classes.wrapperFilterItem} key={filtersBy[index]}>
        <div className={classes.searchWrapper}>
          <div className={classes.searchWrapperFilter}>
            <label className={classes.searchWrapperFilterItem}>
              <b className={classes.searchWrapperLabel}>Filter by</b>
              <Select
                onChange={this.handleFilterChange(index)}
                value={filtersBy[index]}
                style={{ width: "100%" }}
                placeholder={this.props.i18n.t("Select")}
              >
                <Option
                  value="action_type"
                  disabled={filtersBy.includes("action_type")}
                >
                  {this.props.i18n.t("NamespaceAuditLogs:Action type")}
                </Option>
                <Option
                  value="error_level"
                  disabled={filtersBy.includes("error_level")}
                >
                  {this.props.i18n.t("NamespaceAuditLogs:Error level")}
                </Option>
                <Option value="result" disabled={filtersBy.includes("result")}>
                  {this.props.i18n.t("NamespaceAuditLogs:Result")}
                </Option>
                <Option
                  value="log_level"
                  disabled={filtersBy.includes("log_level")}
                >
                  {this.props.i18n.t("NamespaceAuditLogs:Log Level")}
                </Option>
                <Option value="state" disabled={filtersBy.includes("state")}>
                  {this.props.i18n.t("State")}
                </Option>
                <Option value="site" disabled={filtersBy.includes("site")}>
                  {this.props.i18n.t("Site")}
                </Option>
                <Option value="user" disabled={filtersBy.includes("user")}>
                  {this.props.i18n.t("User")}
                </Option>
                <Option
                  value="audit_log_email_type"
                  disabled={filtersBy.includes("audit_log_email_type")}
                >
                  {this.props.i18n.t("NamespaceAuditLogs:Audit log email type")}
                </Option>
              </Select>
            </label>
          </div>

          {filtersBy[index] === "action_type" && (
            <label className={classes.searchWrapperFilterItem}>
              <b className={classes.searchWrapperLabel}>
                {this.props.i18n.t("NamespaceAuditLogs:Result")}
              </b>
              <Select
                mode="multiple"
                showSearch
                onChange={this.handleFilterValueChange(index)}
                value={filtersValue[index]}
                style={{ width: "100%" }}
                placeholder={this.props.i18n.t("Select")}
              >
                {uniq([...Object.keys(actionTypes), ...this.state.activeTypes]).map((key) => (
                // {Object.keys(actionTypes).map((key) => (
                  <Option key={key} value={key}>
                    {key}
                  </Option>
                ))}
              </Select>
            </label>
          )}

          {filtersBy[index] === "error_level" && (
            <label className={classes.searchWrapperFilterItem}>
              <b className={classes.searchWrapperLabel}>
                {this.props.i18n.t("NamespaceAuditLogs:Result")}
              </b>
              <Select
                mode="multiple"
                onChange={this.handleFilterValueChange(index)}
                value={filtersValue[index]}
                style={{ width: "100%" }}
                placeholder={this.props.i18n.t("Select")}
              >
                <Option value="none">{this.props.i18n.t("None")}</Option>
                <Option value="low">
                  {this.props.i18n.t("NamespaceAuditLogs:Low")}
                </Option>
                <Option value="medium">
                  {this.props.i18n.t("NamespaceAuditLogs:Medium")}
                </Option>
                <Option value="high">
                  {this.props.i18n.t("NamespaceAuditLogs:High")}
                </Option>
                <Option value="critical">
                  {this.props.i18n.t("NamespaceAuditLogs:Critical")}
                </Option>
              </Select>
            </label>
          )}

          {filtersBy[index] === "result" && (
            <label className={classes.searchWrapperFilterItem}>
              <b className={classes.searchWrapperLabel}>
                {this.props.i18n.t("NamespaceAuditLogs:Result")}
              </b>
              <Select
                onChange={this.handleFilterValueChange(index)}
                value={filtersValue[index]}
                style={{ width: "100%" }}
                placeholder={this.props.i18n.t("Select")}
              >
                <Option value="success">{this.props.i18n.t("Success")}</Option>
                <Option value="failure">
                  {this.props.i18n.t("NamespaceAuditLogs:Failure")}
                </Option>
                <Option value="info">
                  {this.props.i18n.t("NamespaceAuditLogs:Info")}
                </Option>
                <Option value="debug">
                  {this.props.i18n.t("NamespaceAuditLogs:Debug")}
                </Option>
              </Select>
            </label>
          )}

          {filtersBy[index] === "log_level" && (
            <label className={classes.searchWrapperFilterItem}>
              <b className={classes.searchWrapperLabel}>
                {this.props.i18n.t("NamespaceAuditLogs:Log Level")}
              </b>
              <Select
                onChange={this.handleFilterValueChange(index)}
                value={filtersValue[index]}
                style={{ width: "100%" }}
                placeholder={this.props.i18n.t("Select")}
              >
                <Option value="actions">
                  {this.props.i18n.t("NamespaceAuditLogs:Actions")}
                </Option>
                <Option value="info">
                  {this.props.i18n.t("NamespaceAuditLogs:Info")}
                </Option>
                <Option value="debug">
                  {this.props.i18n.t("NamespaceAuditLogs:Debug")}
                </Option>
              </Select>
            </label>
          )}

          {filtersBy[index] === "state" && (
            <label className={classes.searchWrapperFilterItem}>
              <b className={classes.searchWrapperLabel}>
                {this.props.i18n.t("State")}
              </b>
              <Select
                onChange={this.handleFilterValueChange(index)}
                value={filtersValue[index]}
                style={{ width: "100%" }}
                placeholder={this.props.i18n.t("Select")}
              >
                <Option value="active">{this.props.i18n.t("Active")}</Option>
                <Option value="inactive">
                  {this.props.i18n.t("Inactive")}
                </Option>
              </Select>
            </label>
          )}

          {filtersBy[index] === "audit_log_email_type" && (
            <label className={classes.searchWrapperFilterItem}>
              <b className={classes.searchWrapperLabel}>
                {this.props.i18n.t("NamespaceAuditLogs:Result")}
              </b>
              <Select
                onChange={this.handleFilterValueChange(index)}
                value={filtersValue[index]}
                style={{ width: "100%" }}
                placeholder={this.props.i18n.t("Select")}
              >
                <Option value="sent">{this.props.i18n.t("Sent")}</Option>
                <Option value="not_sent">
                  {this.props.i18n.t("NamespaceAuditLogs:Not sent")}
                </Option>
              </Select>
            </label>
          )}

          {filtersBy[index] === "site" && (
            <label className={classes.searchWrapperFilterItem}>
              <b className={classes.searchWrapperLabel}>
                {this.props.i18n.t("Site")}
              </b>
              <Select
                mode="multiple"
                onChange={this.handleFilterValueChange(index)}
                value={filtersValue[index]}
                style={{ width: "100%" }}
                placeholder={this.props.i18n.t("Select")}
              >
                {sites.map((site) => (
                  <Option key={site.id} value={site.id}>
                    {site.name}
                  </Option>
                ))}
                <Option value={null}>{this.props.i18n.t("None")}</Option>
              </Select>
            </label>
          )}

          {filtersBy[index] === "user" && (
            <label className={classes.searchWrapperFilterItem}>
              <b className={classes.searchWrapperLabel}>
                {this.props.i18n.t("User")}
              </b>
              <Input
                onChange={(e) =>
                  this.handleFilterValueChange(index)(e.target.value)
                }
                style={{ width: "100%" }}
                placeholder={this.props.i18n.t("User")}
              />
            </label>
          )}

          {filtersValue[index] && (
            <span
              className={classes.clearButton}
              onClick={this.clearFilter(index)}
            >
              {this.props.i18n.t("Remove")}
            </span>
          )}
        </div>
      </div>
    );
  };

  render() {
    const {
      errorMessage,
      isLoading,
      responseMessage,
      nameSearch,
      audits,
      currentPage,
      total,
      pageSize,
      auditEdit,
      sortedInfo,
      filtersBy,
      filtersValue,
      sites,
      approvedSelected,
      filterDateFrom,
      filterDateTo,
      includeListEvent,
      checkedItems,
      shiftDown,
      auditDetails,
    } = this.state;

    const logNotAllowedForSelection = (primaryLog, log) =>
      primaryLog && primaryLog.reviewed !== log.reviewed;

    const columns = [
      {
        dataIndex: "checkbox",
        key: "checkbox",
        title: () =>
          this.props.auditLogReviewBulkPermission &&
          audits.length > 0 && (
            <Checkbox
              onChange={(e) => {
                if (e.target.checked) {
                  this.setState(({ checkedItems }) => ({
                    checkedItems: uniq([
                      checkedItems[0] || audits[0],
                      ...audits.reduce(
                        (result, item) =>
                          !logNotAllowedForSelection(
                            checkedItems[0] || audits[0],
                            item
                          )
                            ? [...result, item]
                            : result,
                        []
                      ),
                    ]),
                  }));
                } else {
                  this.setState(({ checkedItems }) => ({
                    checkedItems: [],
                  }));
                }
              }}
            />
          ),
        render: (val, auditItem, index) => (
          <div>
            {this.props.auditLogReviewBulkPermission && (
              <Checkbox
                disabled={logNotAllowedForSelection(checkedItems[0], auditItem)}
                onChange={(e) => {
                  if (checkedItems.length && shiftDown) {
                    const lastCheckedItemIndex = audits.findIndex(
                      (item) =>
                        item.id === checkedItems[checkedItems.length - 1].id
                    );

                    let newItems = [];

                    if (lastCheckedItemIndex < index) {
                      newItems = audits.reduce((result, item, itemIndex) => {
                        return itemIndex > lastCheckedItemIndex &&
                          itemIndex <= index &&
                          !logNotAllowedForSelection(checkedItems[0], item)
                          ? [...result, item]
                          : result;
                      }, []);
                    } else {
                      newItems = audits.reduce((result, item, itemIndex) => {
                        return itemIndex < lastCheckedItemIndex &&
                          itemIndex >= index &&
                          !logNotAllowedForSelection(checkedItems[0], item)
                          ? [...result, item]
                          : result;
                      }, []);
                    }

                    this.setState(({ checkedItems }) => ({
                      checkedItems: uniq([...checkedItems, ...newItems]),
                    }));
                  } else {
                    this.setState(({ checkedItems }) => ({
                      checkedItems: e.target.checked
                        ? uniq([...checkedItems, auditItem])
                        : uniq(
                            checkedItems.filter(
                              (item) => item.id !== auditItem.id
                            )
                          ),
                    }));
                  }
                }}
                checked={
                  checkedItems.findIndex((item) => item.id === auditItem.id) >
                  -1
                }
              />
            )}
          </div>
        ),
      },
      {
        dataIndex: "id",
        key: "id",
        title: this.props.i18n.t("NamespaceAuditLogs:State/ Reviewed"),
        render: (val, record) => (
          <div className={classes.nameAuditWrapper}>
            <div style={{ width: "86px" }}>
              <Badge
                count={record.state}
                style={{
                  backgroundColor:
                    record.state === "active" ? "#52c41a" : "#777",
                }}
              />
              &nbsp;
              <Badge
                count={record.reviewed ? "reviewed" : "unreviewed"}
                style={{
                  backgroundColor: record.reviewed ? "#52c41a" : "#777",
                }}
              />
            </div>
          </div>
        ),
      },
      {
        dataIndex: "site",
        key: "site",
        title: this.props.i18n.t("Site"),
        render: (site) => get(site, "name"),
        sorter: true,
        sortOrder: get(sortedInfo, "columnKey") === "site" && sortedInfo.order,
      },
      {
        dataIndex: "result",
        key: "result",
        title: this.props.i18n.t("NamespaceAuditLogs:Result"),
        render: (val, audit) => (
          <Tooltip placement="topLeft" title={audit.message}>
            <Badge
              count={val}
              style={{
                backgroundColor: logResultTypes[val],
              }}
            />
          </Tooltip>
        ),
        sorter: true,
        sortOrder:
          get(sortedInfo, "columnKey") === "result" && sortedInfo.order,
      },
      {
        dataIndex: "error_level",
        key: "error_level",
        title: this.props.i18n.t("NamespaceAuditLogs:Error level"),
        sorter: true,
        render: (val) => (
          <Badge
            count={val}
            style={{ backgroundColor: errorLevelColors[val] }}
          />
        ),
        sortOrder:
          get(sortedInfo, "columnKey") === "error_level" && sortedInfo.order,
      },
      {
        dataIndex: "user",
        key: "user",
        title: this.props.i18n.t("User"),
        render: (user) => (
          <div
            style={{
              maxWidth: "200px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {get(user, "username")}
          </div>
        ),
        sorter: true,
        sortOrder: get(sortedInfo, "columnKey") === "user" && sortedInfo.order,
      },
      {
        dataIndex: "action_type",
        key: "action_type",
        title: this.props.i18n.t("NamespaceAuditLogs:Action type"),
        render: (val) => val.replace(/_/g, " "),
        sorter: true,
        sortOrder:
          get(sortedInfo, "columnKey") === "action_type" && sortedInfo.order,
      },
      {
        dataIndex: "message",
        key: "message",
        title: this.props.i18n.t("NamespaceAuditLogs:Message"),
        render: (val) => val,
        sorter: true,
        sortOrder:
          get(sortedInfo, "columnKey") === "message" && sortedInfo.order,
      },
      {
        dataIndex: "created",
        key: "created",
        title: this.props.i18n.t("Created"),
        render: (val) => (
          <span>
            {moment
              .utc(val)
              .tz(this.props.userTimezone || "UTC")
              .format("YYYY-MM-DD, HH:mm")}{" "}
            &nbsp;{this.props.userShortTimezone || "UTC"}
          </span>
        ),
        sorter: true,
        sortOrder:
          get(sortedInfo, "columnKey") === "created" && sortedInfo.order,
      },
      {
        dataIndex: "id",
        key: "id_actions",
        title: this.props.i18n.t("Actions"),
        render: (val, audit) => (
          <div className={classes.actions}>
            <Tooltip
              placement="topLeft"
              title={this.props.i18n.t("NamespaceAuditLogs:Edit Audit")}
            >
              <Icon
                type="edit"
                theme="filled"
                className={classes.iconEdit}
                onClick={this.onAuditEdit(audit)}
              />
            </Tooltip>
            &nbsp; &nbsp;
            <div style={{ width: "20px", display: "flex" }}>
              <Tooltip
                placement="topLeft"
                title={this.props.i18n.t("NamespaceAuditLogs:Audit Details")}
              >
                <Icon
                  type="question"
                  theme="outlined"
                  className={classes.iconEdit}
                  onClick={this.onViewAuditDetails(audit)}
                />
              </Tooltip>
            </div>
            &nbsp; &nbsp;
            <div style={{ width: "60px", display: "flex" }}>
              <div>
                <b className={classes.switchLabel}>
                  {this.props.i18n.t("State")}
                </b>
                <br />
                <Tooltip
                  placement="topLeft"
                  title={this.props.i18n.t(
                    "NamespaceAuditLogs:Activate / Deactivate Audit"
                  )}
                >
                  <Switch
                    disabled={
                      (!this.props.auditLogUpdatePermission &&
                        audit.state === "inactive") ||
                      (!this.props.auditLogDeletePermission &&
                        audit.state === "active")
                    }
                    className={classes.reactivateBtn}
                    checked={audit.state === "active"}
                    onChange={this.onSwitchAuditActivation(val, audit.state)}
                  />
                </Tooltip>
              </div>
            </div>
            &nbsp; &nbsp;
            <div style={{ width: "60px", display: "flex" }}>
              <div>
                <b className={classes.switchLabel}>
                  {this.props.i18n.t("NamespaceAuditLogs:Reviewed")}
                </b>
                <br />
                <Tooltip
                  placement="topLeft"
                  title={this.props.i18n.t(
                    "NamespaceAuditLogs:Reviewed / Unreviewed Audit"
                  )}
                >
                  <Switch
                    disabled={!this.props.auditLogReviewPermission}
                    className={classes.reactivateBtn}
                    checked={audit.reviewed}
                    onChange={this.onSwitchAuditReview(val, audit.reviewed)}
                  />
                </Tooltip>
              </div>
            </div>
            {audit.action_type.endsWith("_email") && get(audit, "can_resend") && (
              <div style={{ width: "90px", display: "flex" }}>
                <Tooltip
                  placement="topLeft"
                  title={(get(audit, "reviewed") && get(audit, "comments", "").includes("EMAIL WAS RESENT SUCCESSFULLY"))? this.props.i18n.t("NamespaceAuditLogs:Resend again?") : this.props.i18n.t("NamespaceAuditLogs:Resend email notification")}
                >
                  <Button
                    type={(get(audit, "reviewed") && get(audit, "comments", "").includes("EMAIL WAS RESENT SUCCESSFULLY"))? "default" : "primary"}
                    size="small"
                    className={classes.resendBtn}
                    onClick={this.onResendClick(audit.id)}
                  >
                    {(get(audit, "reviewed") && get(audit, "comments", "").includes("EMAIL WAS RESENT SUCCESSFULLY"))? this.props.i18n.t("NamespaceAuditLogs:Resent successfully") : this.props.i18n.t("NamespaceAuditLogs:Resend")}
                  </Button>
                </Tooltip>
              </div>
            )}
          </div>
        ),
      },
    ];

    return (
      <div>
        <Layout className={classes.parentContainer}>
          <Content className={classes.content}>
            <h3 className="headline-default">
              {this.props.i18n.t("Audit logs")}
            </h3>

            <div className={classes.searchWrapper}>
              <label className={classes.searchWrapperSearch}>
                <b className={classes.searchWrapperLabel}>
                  {this.props.i18n.t("SEARCH")}
                </b>
                <Input.Search
                  enterButton={this.props.i18n.t("Search")}
                  size="large"
                  onSearch={this.onNameSearch}
                  value={nameSearch}
                  onChange={this.onNameChange}
                  style={{ maxWidth: "500px" }}
                />
              </label>

              <span className={classes.clearButton} onClick={this.clearFilters}>
                {this.props.i18n.t("Clear")}
              </span>
            </div>

            <h4 className="headline-default">{this.props.i18n.t("Filter")}</h4>
            <div className={classes.wrapperFilters}>
              <div className={classes.searchWrapperFilterItemApprove}>
                <Checkbox
                  onChange={this.onSelectApprovedStatus("approved")}
                  checked={
                    approvedSelected === null || approvedSelected === true
                  }
                >
                  {this.props.i18n.t("NamespaceAuditLogs:Reviewed")}
                </Checkbox>
                <Checkbox
                  onChange={this.onSelectApprovedStatus("unapproved")}
                  checked={!approvedSelected}
                >
                  {this.props.i18n.t("NamespaceAuditLogs:Unreviewed")}
                </Checkbox>
                <Checkbox
                  onChange={(e) =>
                    this.setState(
                      { includeListEvent: e.target.checked, currentPage: 1 },
                      this.getAudits
                    )
                  }
                  checked={includeListEvent}
                >
                  {this.props.i18n.t(
                    "NamespaceAuditLogs:Skip audit log actions"
                  )}
                </Checkbox>
              </div>
              <div className={classes.searchWrapperFilterItemApprove}>
                <div>
                  {this.props.i18n.t("NamespaceAuditLogs:Date From")}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      marginTop: "4px",
                    }}
                  >
                    <DatePicker
                          disabled={false}
                          format="YYYY/MM/DD"
                          placeholder="YYYY/MM/DD"

                          onChange={(value) => {
                            this.setState(
                              { filterDateFrom: value ? value.format(
                                    "YYYY-MM-DD"
                                  ) : null },
                              this.getAudits
                            )
                          }}
                          showToday={true}
                        />
                  </div>
                </div>
                <div>
                  {this.props.i18n.t("NamespaceAuditLogs:Date to")}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      marginTop: "4px",
                    }}
                  >
                    <DatePicker
                          disabled={false}
                          format="YYYY/MM/DD"
                          placeholder="YYYY/MM/DD"

                          onChange={(value) => {
                            this.setState(
                              { filterDateTo: value ? value.format(
                                    "YYYY-MM-DD"
                                  ) : null },
                              this.getAudits
                            )
                          }}
                          showToday={true}
                        />
                  </div>
                </div>

              </div>

              {filtersBy.map((_, index) => this.renderFilterItem(index))}

              {filtersBy.length === filtersValue.length &&
                filtersBy.length !== 5 &&
                this.renderFilterItem()}
            </div>

            {errorMessage && <Alert type="error" message={errorMessage} />}

            {responseMessage && (
              <Alert type="success" message={responseMessage} />
            )}

            <Table
              columns={columns}
              dataSource={audits}
              loading={isLoading}
              rowKey="id"
              onChange={this.handleTableChange}
              className={classes.table}
              pagination={{
                onShowSizeChange: (current, size) => {
                  this.setState({ pageSize: size });
                },
                current: currentPage,
                total,
                pageSize,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
                position: 'both'
              }}
            />

            {/*{isLoading && <Icon type="loading" />}*/}

            <Modal
              visible={!!auditEdit}
              onCancel={this.onAuditEditHide}
              footer={null}
            >
              {auditEdit && (
                <AuditForm
                  {...auditEdit}
                  isNew={auditEdit && !auditEdit.id}
                  onSubmit={this.onAuditEditSubmit}
                  onCancel={this.onAuditEditHide}
                />
              )}
            </Modal>
            <Modal
              visible={!!auditDetails}
              onCancel={this.onCloseAuditDetails}
              footer={null}
            >
              {auditDetails && (
                <div>
                  <h3>{this.props.i18n.t("Details")}</h3>
                  <div>
                    <h5>{this.props.i18n.t("Action")}</h5>
                    <p>
                      <b>"{auditDetails.action_type}"</b>
                    </p>
                  </div>
                  <div>
                    <h5>{this.props.i18n.t("Action Context")}</h5>
                    <p>
                      {!!auditDetails.created && (
                        <span>
                          {" "}
                          &nbsp;{this.props.i18n.t("at")}&nbsp;
                          <b>
                            {moment
                              .utc(auditDetails.created)
                              .tz(this.props.userTimezone || "UTC")
                              .format("YYYY-MM-DD, HH:mm")}{" "}
                            &nbsp;{this.props.userShortTimezone || "UTC"}
                          </b>
                        </span>
                      )}
                      {!!auditDetails.user && (
                        <span>
                          {" "}
                          &nbsp;{this.props.i18n.t("by")}
                          &nbsp;<b>{get(auditDetails.user, "username")}</b>
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <h5>{this.props.i18n.t("Action result")}</h5>
                    <p>
                      <Badge
                        count={auditDetails.result}
                        style={{
                          backgroundColor: logResultTypes[auditDetails.result],
                        }}
                      />
                      {auditDetails.result === "failure" && (
                        <Badge
                          count={auditDetails.error_level}
                          style={{
                            backgroundColor:
                              errorLevelColors[auditDetails.error_level],
                          }}
                        />
                      )}
                    </p>
                  </div>
                  {!!auditDetails.site && (
                    <div>
                      <h5>{this.props.i18n.t("Site")}</h5>
                      <p>{get(auditDetails.site, "name")}</p>
                    </div>
                  )}
                  <div>
                    <h5>{this.props.i18n.t("Message")}</h5>
                    <p>{auditDetails.message}</p>
                  </div>
                  {!!auditDetails.detailed_log && (
                    <div>
                      <h5>{this.props.i18n.t("Detailed Log")}</h5>
                      <p>{auditDetails.detailed_log}</p>
                    </div>
                  )}
                  {!!auditDetails.comments && (
                    <div>
                      <h5>{this.props.i18n.t("Comments")}</h5>
                      <p>{auditDetails.comments}</p>
                    </div>
                  )}
                </div>
              )}
            </Modal>
          </Content>
        </Layout>
        {this.state.checkedItems.length > 0 && (
          <div
            style={{
              position: "fixed",
              padding: "1rem 3rem",
              borderTop: "1px solid #eee",
              boxShadow: "0 25px 50px 12px rgba(0,0,0,.25)",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 99,
              background: "rgba(255,255,255,0.95)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <p style={{ fontWeight: "bold", fontSize: "0.8rem" }}>
                {this.props.i18n.t("Bulk Actions")}:{" "}
                <span style={{ color: "#999" }}>
                  {this.state.checkedItems.length}{" "}
                  {this.props.i18n.t("selected")}
                </span>
              </p>
              <button
                style={{
                  outline: "none",
                  cursor: "pointer",
                  background: "transparent",
                  padding: 0,
                  margin: 0,
                  border: "none",
                  color: "#aaa",
                }}
                onClick={() => this.setState({ checkedItems: [] })}
              >
                <Icon type="close" />
              </button>
            </div>
            <div style={{ display: "flex" }}>
              <div style={{ display: "flex" }}>
                <span style={{ fontSize: "0.8rem", marginRight: "0.5rem" }}>
                  {this.props.i18n.t("NamespaceAuditLogs:Reviewed")}:
                </span>
                <Switch
                  className={classes.reactivateBtn}
                  checked={checkedItems[0].reviewed}
                  onChange={async (checked) => {
                    this.setState({ isLoading: true });

                    const action = checked
                      ? reviewedAuditLogsBulkRequest
                      : unreviewedAuditLogsBulkRequest;

                    try {
                      await action(
                        this.state.checkedItems.map((item) => item.id)
                      );

                      this.onNameSearch();
                    } catch (e) {
                      if (get(e, "response.status") === 401) {
                        this.props.logout();
                        return;
                      }
                      this.setState({
                        errorMessage: get(e, "response.data.error", "Error"),
                        isLoading: false,
                      });
                    }

                    this.setState({ checkedItems: [] });
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userTimezone: getUserTimezone(state),
  userShortTimezone: getUserShortTimezone(state),
  auditLogDeletePermission: getAuditLogDeletePermission(state),
  auditLogUpdatePermission: getAuditLogUpdatePermission(state),
  auditLogReviewPermission: getAuditLogReviewPermission(state),
  auditLogReviewBulkPermission: getAuditLogReviewBulkPermission(state),
});
const mapDispatchToProps = { logout };

export default isAuthenticated(
  connect(mapStateToProps, mapDispatchToProps)(withNamespaces()(AuditLogs))
);



// WEBPACK FOOTER //
// ./src/containers/AuditLogs/AuditLogs.js